import {useRef} from 'react'
import {useSelector} from 'react-redux'
import {useVioTag} from 'hooks/useVioTag'
import {
  getHasOffersSearchErrored,
  getHasRoomsSearchErrored
} from 'modules/accommodation/selectors'
import {getCurrencyCode} from 'modules/meta/selectors'
import {
  getNumberOfRooms,
  getOfferEntity,
  getShowTotalPrices,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'
import {getTopOffers} from 'modules/sapiSearch/utils'
import {getIsNoDatesSearch} from 'modules/search/selectors'
import {
  getHotelApiRoomsParameters,
  getIsRoomsPollingComplete,
  getIsRoomsRefreshing,
  getUnfilteredRooms
} from 'modules/searchApi/selectors'
import {getCheapestTopOfferIndex} from 'utils/offers'

import {
  getIsAuthenticated,
  selectIsLoading
} from '@vio/core/src/auth/modules/selectors'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'
import {DEAL_FREEZE_OFFER_BOX_EXPERIMENT} from '@vio/shared/src/dealFreeze/components/DealFreezeOfferBox/constants'
import {FromPrice} from '@vio/shared/src/dealFreeze/components/DealFreezeOfferBox/types'
import {
  getFrozenBookingByItinerary,
  getHasCheckedUserLimit,
  getIsDealFreezeEnabled,
  getIsUserEligibleForDealFreeze
} from '@vio/shared/src/dealFreeze/modules/selector'

import {useAccommodationTopOffers} from '../useAccommodationTopOffers'
import {
  DealFreezeOfferBoxScenario,
  getCheapestFreezablePrice,
  getDealFreezeOfferBoxScenario,
  getRenderedTopOffers,
  getVioTopOffer
} from './utils'

export interface DealFreezeOfferBoxState {
  scenario: DealFreezeOfferBoxScenario
  freezableTopOfferId?: string
  isTagVisible: boolean
  isBannerVisible: boolean
  isDecisionPending: boolean
  fromPrice?: FromPrice
}

interface Props {
  hotelId: string
  surface: 'mobile' | 'desktop'
}

export const useDealFreezeOfferBox = ({
  hotelId,
  surface
}: Props): DealFreezeOfferBoxState => {
  const toggle = useToggle()
  const isHydrated = useIsHydrated()
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))
  const splitBookingDetails = useSelector(state =>
    getSplitBookingDetails(state, hotelId)
  )
  const rooms = useSelector(getUnfilteredRooms)
  const isRoomsComplete = useSelector(getIsRoomsPollingComplete)
  const isRoomsRefreshing = useSelector(getIsRoomsRefreshing)
  const lastStateRef = useRef<DealFreezeOfferBoxState>()
  const hasRoomsErrored = useSelector(state =>
    getHasRoomsSearchErrored(state, hotelId)
  )
  const {isComplete: isOffersComplete} = useAccommodationTopOffers({hotelId})
  // The offers query never sets isError, stream failures only land here
  const hasOffersErrored = useSelector(state =>
    getHasOffersSearchErrored(state, hotelId)
  )
  const isNoDatesSearch = useSelector(getIsNoDatesSearch)
  const currency = useSelector(getCurrencyCode)
  const showTotalPrices = useSelector(getShowTotalPrices)
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))
  const {checkIn, checkOut} = useSelector(state =>
    getHotelApiRoomsParameters(state, hotelId)
  )
  const isDealFreezeEnabled = useSelector(getIsDealFreezeEnabled)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const isAuthLoading = useSelector(selectIsLoading)
  const hasCheckedUserLimit = useSelector(getHasCheckedUserLimit)
  const isUserEligible = useSelector(getIsUserEligibleForDealFreeze)
  const frozenItinerary = useSelector(state =>
    getFrozenBookingByItinerary(state, checkIn, checkOut, hotelId)
  )
  const vioTopOffer = getVioTopOffer(
    getRenderedTopOffers(offerEntity, splitBookingDetails),
    splitBookingDetails.splitBookingOffers.map(({offer}) => offer.id)
  )
  const {isVioTagVisible} = useVioTag({offerTags: vioTopOffer?.offer.tags})
  // Desktop tags every exclusive card, mobile only the primary row when it is the cheapest
  const hasOnlyOnVioTag =
    isVioTagVisible &&
    (surface === 'desktop' ||
      (Boolean(vioTopOffer?.isPrimary) &&
        getCheapestTopOfferIndex(getTopOffers(offerEntity)) === 0))
  const cheapestFreezablePrice = getCheapestFreezablePrice(
    rooms,
    showTotalPrices
  )
  const hasFreezableRooms = cheapestFreezablePrice !== undefined
  const scenario = getDealFreezeOfferBoxScenario(vioTopOffer, hasFreezableRooms)

  const isDealFreezeAvailable =
    isHydrated && isDealFreezeEnabled && !frozenItinerary
  const canSeeDealFreeze =
    isDealFreezeAvailable && (!isAuthenticated || isUserEligible)
  // Signed-in eligibility can land after the first offers
  const isEligibilityPending =
    isAuthLoading || (isAuthenticated && !hasCheckedUserLimit)
  // A partial rooms poll can still lose its freezable offers, so nothing
  // allocates until polling is complete
  const isEligible =
    canSeeDealFreeze &&
    isRoomsComplete &&
    scenario !== DealFreezeOfferBoxScenario.None
  // A Vio offer can still arrive in a later offers poll
  const isOffersPending = !isNoDatesSearch && !isOffersComplete
  // An offers failure also stops the rooms search from ever starting
  const isDecisionPending =
    isDealFreezeAvailable &&
    (canSeeDealFreeze || isEligibilityPending) &&
    !hasOffersErrored &&
    (isOffersPending || (!isRoomsComplete && !hasRoomsErrored))

  // Allocate only users who can see the B side
  const isEnabled = isEligible
    ? toggle(DEAL_FREEZE_OFFER_BOX_EXPERIMENT, false, true)
    : false

  const isTagVisible =
    isEnabled && Boolean(vioTopOffer?.isFreezable) && !hasOnlyOnVioTag
  const isBannerVisible = isEnabled
  // Same scaling as the room cards: the stay total already covers every room,
  // the nightly figure is per room even on bundle searches
  const fromPriceValue =
    showTotalPrices || cheapestFreezablePrice === undefined
      ? cheapestFreezablePrice
      : cheapestFreezablePrice * numberOfRooms

  // The periodic rooms refresh empties the rooms until it returns, so keep the last decision
  if (isRoomsRefreshing && lastStateRef.current) return lastStateRef.current

  const state: DealFreezeOfferBoxState = {
    scenario,
    freezableTopOfferId: isTagVisible ? vioTopOffer?.offer.id : undefined,
    isTagVisible,
    isBannerVisible,
    isDecisionPending,
    fromPrice:
      isBannerVisible && !isTagVisible && fromPriceValue !== undefined
        ? {value: fromPriceValue, currency, isTotal: showTotalPrices}
        : undefined
  }
  lastStateRef.current = state
  return state
}
